import { UsersService } from '../api-client/services/UsersService';
import type { UserWithRolesDto } from '../api-client/models/UserWithRolesDto';
import type { RoleName } from '../api-client/models/RoleName';

const CURRENT_USER_STORAGE_KEY = 'currentUser';
const CURRENT_USER_ROLES_STORAGE_KEY = 'currentUserRoles';

class CurrentUserService {
  private currentUser: UserWithRolesDto | null = null;
  private isLoading = false;
  private loadCallbacks: Array<(user: UserWithRolesDto | null) => void> = [];

  // Сохранение данных пользователя в localStorage
  private persistUser(user: UserWithRolesDto | null): void {
    if (user) {
      localStorage.setItem(CURRENT_USER_STORAGE_KEY, JSON.stringify(user));
      const roleNames = user.roles?.map((role) => role.name) || [];
      localStorage.setItem(CURRENT_USER_ROLES_STORAGE_KEY, JSON.stringify(roleNames));
    } else {
      localStorage.removeItem(CURRENT_USER_STORAGE_KEY);
      localStorage.removeItem(CURRENT_USER_ROLES_STORAGE_KEY);
    }
  }

  // Загрузка данных пользователя из localStorage
  private loadPersistedUser(): UserWithRolesDto | null {
    try {
      const stored = localStorage.getItem(CURRENT_USER_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored) as UserWithRolesDto;
      }
    } catch (e) {
      console.error('Failed to load current user from storage', e);
    }
    return null;
  }

  // Подписка на изменения состояния пользователя
  onUserLoad(callback: (user: UserWithRolesDto | null) => void): () => void {
    this.loadCallbacks.push(callback);
    // Сразу вызываем колбэк с текущим состоянием
    if (this.currentUser) {
      callback(this.currentUser);
    }
    return () => {
      this.loadCallbacks = this.loadCallbacks.filter((cb) => cb !== callback);
    };
  }

  // Уведомление подписчиков об изменении состояния
  private notifyCallbacks(): void {
    this.loadCallbacks.forEach((callback) => callback(this.currentUser));
  }

  // Получение текущего пользователя (с кэшированием)
  async getCurrentUser(): Promise<UserWithRolesDto | null> {
    // Возвращаем из памяти, если уже загружен
    if (this.currentUser) {
      return this.currentUser;
    }

    // Пытаемся загрузить из localStorage
    const persistedUser = this.loadPersistedUser();
    if (persistedUser && !this.isLoading) {
      this.currentUser = persistedUser;
      this.notifyCallbacks();
      return this.currentUser;
    }

    // Если идет загрузка, ждем её завершения
    if (this.isLoading) {
      return new Promise((resolve) => {
        const checkLoaded = () => {
          if (!this.isLoading) {
            resolve(this.currentUser);
          } else {
            setTimeout(checkLoaded, 100);
          }
        };
        checkLoaded();
      });
    }

    // Загружаем с сервера
    this.isLoading = true;
    try {
      const response = await UsersService.getCurrentUserApiUsersMeGet();
      this.currentUser = response.data;
      this.persistUser(this.currentUser);
      return this.currentUser;
    } catch (error) {
      console.error('Failed to fetch current user', error);
      // При ошибке пробуем вернуть данные из localStorage
      this.currentUser = this.loadPersistedUser();
      return this.currentUser;
    } finally {
      this.isLoading = false;
      this.notifyCallbacks();
    }
  }

  // Получение ролей текущего пользователя
  async getUserRoles(): Promise<RoleName[]> {
    const user = await this.getCurrentUser();
    return user?.roles?.map((role) => role.name) || [];
  }

  // Проверка наличия конкретной роли
  async hasRole(roleName: RoleName): Promise<boolean> {
    const roles = await this.getUserRoles();
    return roles.includes(roleName);
  }

  // Проверка наличия любой из перечисленных ролей
  async hasAnyRole(roleNames: RoleName[]): Promise<boolean> {
    const roles = await this.getUserRoles();
    return roleNames.some((role) => roles.includes(role));
  }

  // Проверка наличия всех перечисленных ролей
  async hasAllRoles(roleNames: RoleName[]): Promise<boolean> {
    const roles = await this.getUserRoles();
    return roleNames.every((role) => roles.includes(role));
  }

  // Принудительное обновление данных пользователя
  async refreshUser(): Promise<UserWithRolesDto | null> {
    this.isLoading = true;
    try {
      const response = await UsersService.getCurrentUserApiUsersMeGet();
      this.currentUser = response.data;
      this.persistUser(this.currentUser);
      return this.currentUser;
    } catch (error) {
      console.error('Failed to refresh current user', error);
      this.currentUser = null;
      this.persistUser(null);
      return null;
    } finally {
      this.isLoading = false;
      this.notifyCallbacks();
    }
  }

  // Очистка данных пользователя (при выходе)
  clearUser(): void {
    this.currentUser = null;
    this.persistUser(null);
    this.notifyCallbacks();
  }

  // Получение данных пользователя синхронно (из кэша)
  getCachedUser(): UserWithRolesDto | null {
    return this.currentUser || this.loadPersistedUser();
  }

  // Проверка, загружен ли пользователь
  isUserLoaded(): boolean {
    return this.currentUser !== null || this.loadPersistedUser() !== null;
  }

  // Проверка, идет ли загрузка
  getIsLoading(): boolean {
    return this.isLoading;
  }
}

// Экспортируем глобальный экземпляр сервиса
export const currentUserService = new CurrentUserService();

// Экспортируем класс для возможности создания других экземпляров при необходимости
export default CurrentUserService;
